/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useFormContext, FormContext, FormContextProps } from '../hooks/UNSAFE_useFormContext';
import { styles } from './themes/FormLayoutStyles.css';
import { layoutLocalVars } from '../utils/UNSAFE_styles/Layout/LayoutStyles.css';
import { FormLayoutRedwoodTheme } from './themes/redwood/FormLayoutTheme';
import { classNames } from '../utils/UNSAFE_classNames';
import { layoutSpanStyles, LayoutColumns, LayoutColumnSpan } from '../utils/UNSAFE_styles/Layout';
import { useTestId, type TestIdProps } from '#hooks/UNSAFE_useTestId';

type Props = TestIdProps & {
  /**
   * Specifies if the FormLayout will dynamically adjust its columns to match the container size based on
   * defined container breakpoints.
   */
  columnBehavior?: 'responsive' | 'fixed';

  /**
   * The number of columns to display. In a 'responsive' layout, you will have the specified number of
   * columns or fewer, depending on the defined container breakpoints.
   */
  columns?: LayoutColumns;

  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'.
   */
  columnSpan?: LayoutColumnSpan;

  /**
   * The direction the children should be laid out, row first or column first.
   */
  direction?: 'row' | 'column';

  /**
   * Indicates if the form layout will use 100% of the container's width, and will ignore the theme's max column width.
   */
  isFullWidth?: boolean;

  /**
   * Indicates whether the form layout is readonly.
   */
  isReadonly?: FormContextProps['isReadonly'];

  /**
   * Specifies the label position.
   */
  labelEdge?: FormContextProps['labelEdge'];

  /**
   * Specifies the width of the start aligned label, ignored for top & inside aligned.
   */
  labelStartWidth?: FormContextProps['labelStartWidth'];

  /**
   * Specifies if start or top label text should wrap or truncate.
   */
  labelWrapping?: FormContextProps['labelWrapping'];

  /**
   * Specifies the density of the user assistance presentation.
   */
  userAssistanceDensity?: FormContextProps['userAssistanceDensity'];

  children?: ComponentChildren;
};

export const FormLayout = ({
  columns = 1,
  columnBehavior = 'responsive',
  columnSpan = 1,
  direction = 'row',
  isFullWidth,
  isReadonly: propIsReadonly,
  labelEdge: propLabelEdge,
  labelStartWidth: propLabelStartWidth,
  labelWrapping: propLabelWrapping,
  userAssistanceDensity: propUserAssistanceDensity,
  children,
  testId
}: Props) => {
  const {
    isDisabled,
    isFormLayout: formIsFormLayout,
    isReadonly: isFormReadonly,
    labelEdge: formLabelEdge,
    labelStartWidth: formLabelStartWidth,
    labelWrapping: formLabelWrapping,
    textAlign,
    userAssistanceDensity: formUserAssistanceDensity
  } = useFormContext();
  const testIdProps = useTestId(testId);
  const layoutElementTestIdProps = useTestId(`${testId}_layout`);
  const isReadonly = propIsReadonly ?? isFormReadonly;
  const labelEdge = propLabelEdge ?? formLabelEdge;
  const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
  const labelWrapping = propLabelWrapping ?? formLabelWrapping;
  // If we have the prop, pass it on, or if we are in a form layout, use the context value, otherwise default to 'efficient'
  const userAssistanceDensity =
    propUserAssistanceDensity ?? (formIsFormLayout ? formUserAssistanceDensity : 'efficient');

  // We need to set the localVars.columnCount so that we can correctly set the max width to the current column count
  const assignVarStyles = assignInlineVars({
    [layoutLocalVars.columnCount]: `${columns}`
  });
  const { baseTheme, classes } = useComponentTheme(FormLayoutRedwoodTheme, {
    columnBehavior,
    direction
  });

  return (
    <FormContext.Provider
      value={{
        isDisabled,
        isFormLayout: true,
        isReadonly,
        labelEdge,
        labelStartWidth,
        labelWrapping,
        textAlign,
        userAssistanceDensity
      }}>
      <div
        class={classNames([
          baseTheme,
          styles.rootWrapperStyle,
          isFullWidth && styles.rootWrapperFullWidthStyle,
          layoutSpanStyles.layoutSpanColumn[columnSpan]
        ])}
        {...testIdProps}
        style={assignVarStyles}>
        <div class={classes} {...layoutElementTestIdProps}>
          {children}
        </div>
      </div>
    </FormContext.Provider>
  );
};
